import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import HebrewKeyboard from "@/components/HebrewKeyboard";
import NoIndex from "@/components/NoIndex";
import useLiveStream from "@/lib/useLiveStream";
import OnStageBurst from "@/components/OnStageBurst";
import { logDebug } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY = (slug) => `live_participant_${slug}`;
// The participant token is a bearer credential, so it lives in sessionStorage:
// it survives a refresh mid-show but dies with the tab, and never lingers on a
// borrowed or shared phone. The server also expires it after 24h.
const store = {
  get(key, fallback) {
    try {
      return JSON.parse(sessionStorage.getItem(key) ?? "null") ?? fallback;
    } catch (err) {
      logDebug("session read failed", err);
      return fallback;
    }
  },
  set(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      logDebug("session write failed (private mode?)", err);
    }
  },
  clear(key) {
    try {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    } catch (err) {
      logDebug("session clear failed", err);
    }
  },
};

/**
 * Mobile-first audience page.
 *   Step 1: name + email gate (kept in sessionStorage per event slug)
 *   Step 2: word submission — no playback, no save. Submissions stream
 *           live to the moderator via SSE.
 * Mode (english / hebrew) mirrors the event mode set by the moderator,
 * so scanning the QR code at any point in the show puts you in the right
 * alphabet.
 */
export default function LiveAudience() {
  const { slug } = useParams();
  const [loadErr, setLoadErr] = useState("");
  const [participant, setParticipant] = useState(() => store.get(STORAGE_KEY(slug), null));
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [joining, setJoining] = useState(false);
  const [words, setWords] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showHebrewKb, setShowHebrewKb] = useState(false);
  const [recentSent, setRecentSent] = useState([]);
  // Submission ids sent from this phone (persisted so a refresh mid-show
  // doesn't lose the "you're on stage" match).
  const [mySubIds, setMySubIds] = useState(() => store.get(`live_subs_${slug}`, []));

  // One authoritative event (see useLiveStream): the initial fetch, the live
  // stream and the reconcile poll all merge into it monotonically, so a
  // dropped frame can't leave a phone on the wrong scene.
  const { mode, event, applyEvent, lastSelection, resetAt } = useLiveStream(slug, { pollMs: 15000 });
  const effectiveEvent = event;
  const effectiveMode = mode || event?.mode || "english";
  const isHebrew = effectiveMode === "hebrew";

  // Presentation state — when Dan advances the show to the conclusion
  // slide, the audience form is swapped for a thank-you view so nobody
  // submits after the show is over. `current_slide_key` streams in via
  // SSE (`event_updated`), so this transitions live without a reload.
  const currentSlideKey = effectiveEvent?.current_slide_key;
  const currentSlide = (effectiveEvent?.slides || []).find((s) => s.key === currentSlideKey);
  const isConclusion = currentSlide?.type === "conclusion";

  // Regex of characters ALLOWED for the current mode. Everything else is
  // stripped as the user types, so a Hebrew show never ends up with
  // "hello" on the projector (and vice versa). Space is always allowed.
  const allowedRe = isHebrew ? /[^\u0590-\u05FF\s'"\u2019\u05F3]/g : /[^A-Za-z\s'\u2019]/g;
  const wrongScript = isHebrew ? /[A-Za-z]/ : /[\u0590-\u05FF]/;
  const sanitize = (text) => text.replace(allowedRe, "");
  const [scriptHint, setScriptHint] = useState(false);

  const onWordsChange = (raw) => {
    if (wrongScript.test(raw)) {
      setScriptHint(true);
      // Bring the on-screen Hebrew keyboard into view since the user is
      // clearly reaching for it (probably typing English on a phone).
      if (isHebrew) setShowHebrewKb(true);
    }
    setWords(sanitize(raw));
  };

  useEffect(() => {
    if (!scriptHint) return;
    const t = setTimeout(() => setScriptHint(false), 2200);
    return () => clearTimeout(t);
  }, [scriptHint]);

  // When the moderator flips the mode mid-show, wipe whatever's in the
  // input so a leftover English word doesn't get submitted into Hebrew mode.
  useEffect(() => {
    setWords((w) => sanitize(w));
  }, [isHebrew]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/live/${slug}`);
        if (!cancelled) applyEvent(data);
      } catch (e) {
        if (!cancelled) setLoadErr(e.response?.status === 404 ? "This event doesn't exist." : "Couldn't load the event.");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const join = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email please.");
      return;
    }
    setJoining(true);
    try {
      const { data } = await axios.post(`${API}/live/${slug}/join`, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
      });
      const stored = { token: data.participant_token, name: data.name };
      store.set(STORAGE_KEY(slug), stored);
      setParticipant(stored);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setJoining(false);
    }
  };

  const wordCount = words.split(/\s+/).filter(Boolean).length;

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = words.split(/\s+/).map((w) => w.trim()).filter(Boolean).slice(0, 5);
    if (cleaned.length === 0) {
      toast.error("Type at least one word.");
      return;
    }
    setSubmitting(true);
    // Venue wifi drops packets — retry transport failures a couple of
    // times before telling them it didn't go through.
    const post = async (tries = 3) => {
      try {
        return await axios.post(`${API}/live/${slug}/submissions`, {
          words: cleaned,
          participant_token: participant.token,
        }, { timeout: 8000 });
      } catch (err) {
        const transport = !err.response; // no HTTP response = network/timeout
        if (transport && tries > 1) {
          await new Promise((r) => setTimeout(r, 900));
          return post(tries - 1);
        }
        throw err;
      }
    };
    try {
      const { data } = await post();
      if (data?.id) {
        setMySubIds((prev) => {
          const next = [data.id, ...prev].slice(0, 40);
          store.set(`live_subs_${slug}`, next);
          return next;
        });
      }
      setRecentSent((prev) => [cleaned.join(" "), ...prev].slice(0, 5));
      setWords("");
      toast.success("Sent to Dan.");
    } catch (e) {
      if (e.response?.status === 401) {
        store.clear(STORAGE_KEY(slug));
        setParticipant(null);
      }
      toast.error(
        formatApiErrorDetail(e) ||
          (e.response ? "Didn't go through. Try again." : "Weak signal — your word is still here, tap send again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const insertHebrew = (ch) => setWords((w) => w + ch);
  const backspaceHebrew = () => setWords((w) => w.slice(0, -1));

  // Admin cleared rehearsal data — this phone's participant row is gone,
  // so forget it rather than letting the next send fail with a 401.
  useEffect(() => {
    if (!resetAt) return;
    store.clear(STORAGE_KEY(slug));
    store.clear(`live_subs_${slug}`);
    setParticipant(null);
    setMySubIds([]);
    setRecentSent([]);
  }, [resetAt, slug]);

  // "You're on the big screen" payoff: when Dan pushes a word that came
  // from THIS phone, take the screen over for a few seconds.
  const [onStage, setOnStage] = useState(null);
  const shownRef = React.useRef(null);
  useEffect(() => {
    if (!lastSelection || lastSelection.source !== "audience") return;
    if (shownRef.current === lastSelection.id) return;
    const mine =
      (lastSelection.submission_id && mySubIds.includes(lastSelection.submission_id)) ||
      (!lastSelection.submission_id &&
        participant?.name &&
        lastSelection.participant_name === participant.name);
    if (!mine) return;
    shownRef.current = lastSelection.id;
    setOnStage({ words: lastSelection.words.join(" "), mode: lastSelection.mode });
  }, [lastSelection, mySubIds, participant?.name]);

  if (loadErr) {
    return (
      <div className="min-h-screen w-full grid place-items-center p-6" style={{ backgroundColor: "#080419", color: "#F5EFE3", fontFamily: "'Courier Prime', monospace" }}>
        <NoIndex title="Compose Yourself! by Dan Berry" />
        <p data-testid="live-audience-error" className="text-center">{loadErr}</p>
      </div>
    );
  }
  if (!event) {
    return (
      <div className="min-h-screen w-full grid place-items-center" style={{ backgroundColor: "#080419", color: "#F5EFE3" }}>
        <NoIndex title="Compose Yourself! by Dan Berry" /><p className="text-sm opacity-60">Loading…</p>
      </div>
    );
  }

  const notAcceptingWord = !event.is_active;
  // The floor is only open while Dan is on an audience-facing slide
  // (Act 2 Scene 3's QR + app slides, Act 3 Scene 2's app slide).
  const submissionsOpen =
    !currentSlide || currentSlide.type === "qr" || currentSlide.type === "app";

  return (
    <div className="min-h-screen w-full" style={{ backgroundColor: "#080419", color: "#F5EFE3", fontFamily: "'Courier Prime', monospace" }} data-live-dark="" data-testid="live-audience-page">
      <NoIndex title="Compose Yourself! by Dan Berry" />
      <Toaster position="top-center" richColors closeButton />
      <OnStageBurst
        words={onStage?.words}
        mode={onStage?.mode}
        onDone={() => setOnStage(null)}
      />

      <main className="max-w-[560px] mx-auto px-5 pt-8 pb-24" dir={isHebrew && participant ? "rtl" : "ltr"}>
        <header className="text-center mb-6" dir="ltr">
          <p className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "#F2D589" }}>Live · {isHebrew ? "עברית" : "English"}</p>
          <h1 className="text-3xl sm:text-4xl mt-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, textWrap: "balance" }} data-testid="live-audience-title">
            {event.name}
          </h1>
        </header>

        {isConclusion ? (
          <div className="rounded-2xl border p-8 text-center space-y-4" style={{ borderColor: "rgba(232,196,138,0.35)", backgroundColor: "rgba(8,4,25,0.55)" }} data-testid="live-audience-conclusion">
            <p className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "#F2D589" }}>Thank you</p>
            <h2 className="text-3xl sm:text-4xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              That&apos;s a wrap.
            </h2>
            <p className="text-base opacity-80" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
              A recording of tonight&apos;s performance will be available soon.
            </p>
            <p className="text-sm opacity-60">Keep an eye on your inbox &mdash; Dan will send it your way.</p>
          </div>
        ) : !participant ? (
          <form onSubmit={join} className="rounded-2xl border p-6 backdrop-blur-md space-y-3" style={{ borderColor: "rgba(232,196,138,0.35)", backgroundColor: "rgba(8,4,25,0.55)" }} data-testid="live-audience-gate">
            <p className="text-sm leading-relaxed opacity-80">Say hi so Dan knows who&apos;s in the room. We won&apos;t spam you &mdash; just occasional notes from Dan.</p>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="First name" autoComplete="given-name" required className="h-12 bg-white/[0.06] border-white/15 text-white placeholder:text-white/40" data-testid="live-audience-name" />
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" autoComplete="email" required className="h-12 bg-white/[0.06] border-white/15 text-white placeholder:text-white/40" data-testid="live-audience-email" />
            <Button type="submit" disabled={joining} data-testid="live-audience-join" className="w-full h-12 rounded-full" style={{ backgroundColor: "transparent", color: "#F2D589", border: "1px solid #F2D589", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              {joining ? "\u2026" : "I\u2019m in \u2192"}
            </Button>
          </form>
        ) : notAcceptingWord ? (
          <div className="rounded-2xl border p-6 text-center" style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,25,0.45)" }} data-testid="live-audience-paused">
            <p className="text-lg" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>Hang tight &mdash; Dan hasn&apos;t opened the floor yet.</p>
          </div>
        ) : !submissionsOpen ? (
          <div className="rounded-2xl border p-8 text-center space-y-3" style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,25,0.45)" }} data-testid="live-audience-closed">
            <p className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "#F2D589" }}>Intermission</p>
            <p className="text-xl leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
              No submissions right now, but you&apos;ll be back here in a few minutes for more fun!
            </p>
          </div>
        ) : (
          <>
            <form onSubmit={submit} className="rounded-2xl border p-5 sm:p-6 backdrop-blur-md space-y-3" style={{ borderColor: "rgba(232,196,138,0.35)", backgroundColor: "rgba(8,4,25,0.55)" }} data-testid="live-audience-form">
              <label className="block text-[10px] uppercase tracking-[0.22em] opacity-70" dir="ltr">Type up to 5 words</label>
              <Input
                value={words}
                onChange={(e) => onWordsChange(e.target.value)}
                placeholder={isHebrew ? "שלום אהבה" : "hello world"}
                autoFocus
                dir={isHebrew ? "rtl" : "ltr"}
                inputMode={isHebrew ? "text" : "text"}
                className="h-14 text-xl bg-white/[0.08] border-white/20 text-white placeholder:text-white/30"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                data-testid="live-audience-input"
              />
              {scriptHint && (
                <p
                  className="text-xs text-center rounded-md px-3 py-2"
                  style={{
                    color: "#F2D589",
                    backgroundColor: "rgba(232,196,138,0.08)",
                    borderLeft: "2px solid #F2D589",
                  }}
                  dir="ltr"
                  data-testid="live-audience-script-hint"
                >
                  {isHebrew
                    ? "This song is in Hebrew \u2014 only Hebrew letters get through. Use the Hebrew keyboard below instead!"
                    : "This song is in English \u2014 only English letters get through."}
                </p>
              )}
              {isHebrew && (
                <div className="flex gap-2 items-center" dir="ltr">
                  <Button type="button" variant="outline" onClick={() => setShowHebrewKb((s) => !s)} className="h-9 rounded-full border-white/20 text-xs" data-testid="live-audience-hebrew-keyboard-toggle">
                    {showHebrewKb ? "Hide keyboard" : "Hebrew keyboard"}
                  </Button>
                </div>
              )}
              {showHebrewKb && isHebrew && (
                <div dir="ltr">
                  <HebrewKeyboard onLetter={insertHebrew} onSpace={() => insertHebrew(" ")} onBackspace={backspaceHebrew} />
                </div>
              )}
              <div className="flex items-center justify-between text-xs opacity-70" dir="ltr">
                <span>{wordCount} / 5 words</span>
                <span>Hi, {participant.name}</span>
              </div>
              <Button type="submit" disabled={submitting} data-testid="live-audience-submit" className="w-full h-14 rounded-full" style={{ backgroundColor: "transparent", color: "#F2D589", border: "1px solid #F2D589", fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", fontSize: "14px" }}>
                {submitting ? "Sending…" : "Send to Dan →"}
              </Button>
            </form>

            {recentSent.length > 0 && (
              <section className="mt-6" dir="ltr" data-testid="live-audience-sent-list">
                <p className="text-[10px] uppercase tracking-[0.28em] opacity-60 mb-2">You&apos;ve sent</p>
                <ul className="space-y-1.5">
                  {recentSent.map((w, i) => (
                    <li key={`${w}-${i}`} className="text-sm rounded-md px-3 py-2 border" style={{ borderColor: "rgba(255,255,255,0.08)", backgroundColor: "rgba(8,4,25,0.4)" }}>{w}</li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
